import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, AlertTriangle, CheckCircle2, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';

/**
 * TT-PRODUCT-IMAGE-GALLERY-001 — Admin product image gallery.
 * Backend: POST /api/admin/product-images/upload (one file at a time; frontend loops).
 * Accepts JPG/PNG/WEBP up to 5 MB each, max 5 images per product (backend also enforces).
 * Props:
 *   images: string[]  — array of image URLs (index 0 = main)
 *   onChange: (nextImages: string[]) => void
 *   maxImages?: number (default 5)
 */
const MAX_IMAGES_DEFAULT = 5;
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

const ProductImageUploadBox = ({ images, onChange, maxImages = MAX_IMAGES_DEFAULT }) => {
  const list = Array.isArray(images) ? images : [];
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef(null);

  const clearMessages = () => { setError(''); setSuccess(''); };
  const remaining = Math.max(0, maxImages - list.length);
  const canUploadMore = remaining > 0;

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await axios.post(`${API_URL}/api/admin/product-images/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (!r.data?.image_url) throw new Error('image_url missing');
    return r.data.image_url;
  };

  const handleFiles = useCallback(async (fileList) => {
    clearMessages();
    if (busy) return;
    const arr = Array.from(fileList || []).filter(Boolean);
    if (arr.length === 0) return;

    if (arr.length > remaining) {
      setError(`En fazla ${maxImages} ürün görseli yükleyebilirsiniz.`);
      return;
    }

    // Client-side pre-validation
    for (const f of arr) {
      if (!ALLOWED_MIME.includes(f.type)) {
        setError('Lütfen JPG, PNG veya WEBP formatında bir görsel yükleyin.');
        return;
      }
      if (f.size > MAX_BYTES) {
        setError('Görsel boyutu en fazla 5 MB olabilir.');
        return;
      }
    }

    setBusy(true);
    setProgress({ done: 0, total: arr.length });
    const uploaded = [];
    try {
      for (let i = 0; i < arr.length; i++) {
        // Re-check gallery cap on each iteration (in case props change)
        if (list.length + uploaded.length >= maxImages) {
          setError(`En fazla ${maxImages} ürün görseli yükleyebilirsiniz.`);
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadOne(arr[i]);
        uploaded.push(url);
        setProgress({ done: i + 1, total: arr.length });
      }
      if (uploaded.length > 0) {
        const next = [...list, ...uploaded].slice(0, maxImages);
        onChange(next);
        setSuccess(uploaded.length === 1 ? 'Görsel yüklendi.' : 'Görseller yüklendi.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || 'Görsel yüklenemedi. Lütfen tekrar deneyin.');
      if (uploaded.length > 0) {
        // Keep whatever succeeded
        const next = [...list, ...uploaded].slice(0, maxImages);
        onChange(next);
      }
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0 });
    }
  }, [busy, list, maxImages, onChange, remaining]);

  const onDrop = useCallback((ev) => {
    ev.preventDefault();
    setDragOver(false);
    if (busy || !canUploadMore) return;
    const files = ev.dataTransfer?.files;
    if (!files || files.length === 0) return;
    handleFiles(files);
  }, [busy, canUploadMore, handleFiles]);

  const removeAt = (idx) => {
    clearMessages();
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  };

  const setAsMain = (idx) => {
    clearMessages();
    if (idx === 0 || idx >= list.length) return;
    const next = [list[idx], ...list.filter((_, i) => i !== idx)];
    onChange(next);
  };

  const move = (idx, direction) => {
    clearMessages();
    const j = idx + direction;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid="product-image-upload-box" className="md:col-span-2">
      <label className="block text-xs font-bold mb-1 text-neutral-600">Görsel Galerisi ({list.length}/{maxImages})</label>
      <div
        onDragOver={(e) => { e.preventDefault(); if (canUploadMore && !busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => (canUploadMore && !busy) && inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-md p-6 text-center transition ${
          dragOver ? 'border-emerald-500 bg-emerald-50'
          : busy ? 'border-neutral-200 bg-neutral-50'
          : !canUploadMore ? 'border-neutral-200 bg-neutral-50 cursor-not-allowed'
          : 'border-neutral-300 bg-white hover:border-neutral-500'
        }`}
        data-testid="product-image-dropzone"
        aria-disabled={!canUploadMore || busy}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          disabled={!canUploadMore || busy}
          className="hidden"
          data-testid="product-image-file-input"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) handleFiles(files);
            e.target.value = '';
          }}
        />
        {busy ? (
          <div className="flex flex-col items-center gap-2 py-2 text-neutral-600">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-xs font-semibold">Yükleniyor{progress.total > 1 ? `… (${progress.done}/${progress.total})` : '…'}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-neutral-500">
            <Upload className="w-6 h-6" />
            <p className="text-xs font-bold text-neutral-700">
              {canUploadMore ? 'Sürükle-bırak veya tıklayarak seç' : 'Görsel limitine ulaşıldı'}
            </p>
            <p className="text-[10px] text-neutral-400">
              JPG, PNG veya WEBP — maksimum 5 MB — en fazla {maxImages} görsel
            </p>
            {canUploadMore && (
              <p className="text-[10px] text-neutral-400">Kalan: {remaining} görsel</p>
            )}
          </div>
        )}
      </div>

      {list.length > 0 && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="product-image-gallery-grid">
          {list.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              data-testid={`product-image-thumb-${idx}`}
              className={`relative border rounded overflow-hidden bg-neutral-50 ${idx === 0 ? 'border-emerald-500 ring-1 ring-emerald-400' : 'border-neutral-200'}`}
            >
              <div className="aspect-square w-full">
                <img
                  src={url}
                  alt={`Ürün görseli ${idx + 1}`}
                  className="w-full h-full object-cover"
                  data-testid={`product-image-thumb-img-${idx}`}
                />
              </div>
              {idx === 0 && (
                <div className="absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white" data-testid={`product-image-main-badge-${idx}`}>
                  <Star className="w-2.5 h-2.5" fill="currentColor" /> Ana görsel
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white flex items-center justify-between px-1 py-1 gap-1">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    title="Yukarı"
                    className={`p-1 rounded ${idx === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/20'}`}
                    data-testid={`product-image-move-up-${idx}`}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === list.length - 1}
                    title="Aşağı"
                    className={`p-1 rounded ${idx === list.length - 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/20'}`}
                    data-testid={`product-image-move-down-${idx}`}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {idx !== 0 && (
                    <button
                      type="button"
                      onClick={() => setAsMain(idx)}
                      title="Ana görsel yap"
                      className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-emerald-500 hover:bg-emerald-600"
                      data-testid={`product-image-set-main-${idx}`}
                    >
                      Ana yap
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    title="Kaldır"
                    className="p-1 rounded bg-red-600 hover:bg-red-700"
                    data-testid={`product-image-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-2 text-xs font-semibold text-red-700 flex items-start gap-1" data-testid="product-image-error">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {error}
        </p>
      )}
      {success && !error && (
        <p className="mt-2 text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid="product-image-success">
          <CheckCircle2 className="w-3.5 h-3.5" /> {success}
        </p>
      )}
    </div>
  );
};

export default ProductImageUploadBox;
